import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import trash from '../../assests/trash.svg';
import './invoices.css';

const InvoiceData = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    invoiceType: 'sales_invoice',
    customerName: '',
    customerId: '',
    customerPhone: '',
    customerAddress: '',
    paymentMethod: 'cash',
    notes: '',
    originalInvoiceNumber: '',
    creditNoteReason: ''
  });

  const [items, setItems] = useState([
    { id: 1, name: '', quantity: 1, unitPrice: 0, total: 0 }
  ]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
  }, [token, i18n.language]);

  const fetchCustomers = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/patients', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      if (result.status) {
        setCustomers(result.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/services', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify({}),
      });

      const result = await response.json();
      if (result.status) {
        setProducts(result.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomerSelect = (e) => {
    const customerId = e.target.value;
    const customer = customers.find(c => c.id == customerId);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customerName: customer.name || '',
        customerId: customer.id || '',
        customerPhone: customer.mobile || '',
        customerAddress: customer.address || ''
      }));
    }
  };

  const addItem = () => {
    const newId = Math.max(...items.map(item => item.id)) + 1;
    setItems([...items, { id: newId, name: '', quantity: 1, unitPrice: 0, total: 0 }]);
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const handleItemChange = (id, field, value) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unitPrice') {
          updatedItem.total = updatedItem.quantity * updatedItem.unitPrice;
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const handleProductSelect = (id, productId) => {
    const product = products.find(p => p.id == productId);
    if (product) {
      handleItemChange(id, 'name', product.name || '');
      handleItemChange(id, 'unitPrice', product.price || 0);
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateVAT = () => {
    return calculateSubtotal() * 0.15;
  };

  const calculateGrandTotal = () => {
    return calculateSubtotal() + calculateVAT();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const invoiceData = {
        type: formData.invoiceType,
        customer_name: formData.customerName,
        customer_id: formData.customerId,
        customer_phone: formData.customerPhone,
        customer_address: formData.customerAddress,
        payment_method: formData.paymentMethod,
        notes: formData.notes,
        original_invoice_number: formData.originalInvoiceNumber,
        credit_note_reason: formData.creditNoteReason,
        items: items.map(item => ({
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          total: item.total
        })),
        subtotal: calculateSubtotal(),
        vat: calculateVAT(),
        grand_total: calculateGrandTotal()
      };

      const response = await fetch('https://naql.nozzm.com/api/add_invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify(invoiceData),
      });

      const result = await response.json();
      if (result.status) {
        navigate('/Invoices', { state: { updated: true } });
      } else {
        setError(result.message || 'Failed to create invoice');
      }
    } catch (error) {
      setError('Error creating invoice');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container tables bg-white mt-5">
      <div className="tableTitle">
        <h3>{t('invoices.addInvoice')}</h3>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Invoice Type */}
          <div className="col-md-6 mb-3">
            <label className="form-label">{t('invoices.invoiceType')}</label>
            <select
              className="form-select"
              name="invoiceType"
              value={formData.invoiceType}
              onChange={handleInputChange}
              required
            >
              <option value="sales_invoice">{t('invoices.salesInvoice')}</option>
              <option value="credit_note">{t('invoices.creditNote')}</option>
            </select>
          </div>

          {/* Customer Selection */}
          <div className="col-md-6 mb-3">
            <label className="form-label">{t('invoices.selectCustomer')}</label>
            <select
              className="form-select"
              onChange={handleCustomerSelect}
            >
              <option value="">{t('invoices.selectCustomer')}</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} - {customer.mobile}
                </option>
              ))}
            </select>
          </div>

          {/* Customer Details */}
          <div className="col-md-6 mb-3">
            <label className="form-label">{t('invoices.customerName')}</label>
            <input
              type="text"
              className="form-control"
              name="customerName"
              value={formData.customerName}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">{t('invoices.customerId')}</label>
            <input
              type="text"
              className="form-control"
              name="customerId"
              value={formData.customerId}
              onChange={handleInputChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">{t('invoices.customerPhone')}</label>
            <input
              type="text"
              className="form-control"
              name="customerPhone"
              value={formData.customerPhone}
              onChange={handleInputChange}
            />
          </div>

          <div className="col-md-6 mb-3">
            <label className="form-label">{t('invoices.paymentMethod')}</label>
            <select
              className="form-select"
              name="paymentMethod"
              value={formData.paymentMethod}
              onChange={handleInputChange}
              required
            >
              <option value="cash">{t('invoices.cash')}</option>
              <option value="card">{t('invoices.card')}</option>
              <option value="bankTransfer">{t('invoices.bankTransfer')}</option>
            </select>
          </div>

          <div className="col-md-12 mb-3">
            <label className="form-label">{t('invoices.customerAddress')}</label>
            <textarea
              className="form-control"
              name="customerAddress"
              value={formData.customerAddress}
              onChange={handleInputChange}
              rows="2"
            />
          </div>

          {/* Credit Note Specific Fields */}
          {formData.invoiceType === 'credit_note' && (
            <>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t('invoices.originalInvoice')}</label>
                <input
                  type="text"
                  className="form-control"
                  name="originalInvoiceNumber"
                  value={formData.originalInvoiceNumber}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">{t('invoices.reason')}</label>
                <input
                  type="text"
                  className="form-control"
                  name="creditNoteReason"
                  value={formData.creditNoteReason}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </>
          )}

          {/* Items Section */}
          <div className="col-12 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>{t('invoices.items')}</h5>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={addItem}
              >
                {t('invoices.addItem')}
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>{t('invoices.itemName')}</th>
                    <th>{t('invoices.quantity')}</th>
                    <th>{t('invoices.unitPrice')}</th>
                    <th>{t('invoices.total')}</th>
                    <th>{t('control')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <select
                          className="form-select"
                          value={item.name}
                          onChange={(e) => handleProductSelect(item.id, e.target.value)}
                        >
                          <option value="">{t('invoices.selectItem')}</option>
                          {products.map(product => (
                            <option key={product.id} value={product.name}>
                              {product.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                          min="1"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={item.total}
                          readOnly
                        />
                      </td>
                      <td>
                        {items.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => removeItem(item.id)}
                          >
                            <img src={trash} alt="Remove" style={{ width: '16px', height: '16px' }} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="col-md-6 offset-md-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <span>{t('invoices.subtotal')}:</span>
                  <span>{calculateSubtotal().toFixed(2)} ريال</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>{t('invoices.vat')}:</span>
                  <span>{calculateVAT().toFixed(2)} ريال</span>
                </div>
                <hr />
                <div className="d-flex justify-content-between fw-bold">
                  <span>{t('invoices.grandTotal')}:</span>
                  <span>{calculateGrandTotal().toFixed(2)} ريال</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="col-12 mb-3">
            <label className="form-label">{t('invoices.notes')}</label>
            <textarea
              className="form-control"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows="3"
            />
          </div>

          {/* Buttons */}
          <div className="col-12">
            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'جاري الحفظ...' : t('invoices.save')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/Invoices')}
              >
                {t('invoices.cancel')}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default InvoiceData;
